import { ReactNode, useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { Lead, Panel, PanelHeader, PanelBody } from '../ui/Panel';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import MoreMenu from '../ui/MoreMenu';
import Pill from '../ui/Pill';
import { EmptyState, ErrorNotice, InfoNotice, SkeletonRows } from '../ui/States';
import { fmt } from '../../lib/format';
import { dayLabel, torontoToday } from '../../lib/events';
import { errorMessage, inputClass, money } from '../../lib/programs';
import { openBlankTab, showPrintable } from '../../lib/printable';
import type { SimplePanelProps } from './adapt';
import { confirmAction, promptAction } from '../ui/Confirm';
import AddLink from '../ui/AddLink';
import { usePanelShown } from './pageContext';
import { em, headRow, num, td, th, weekDay } from './logistics';
import {
  Accommodation,
  AccStatus,
  BoardDay,
  DayOf,
  Decision,
  DECISION_TONE,
  Leg,
  Printable,
  STATUS_LABEL,
  Station,
  TravelException,
  stack,
  useDayOf,
  useDayOfWrite,
  useMarkPresent,
  usePresent,
} from './dayOfTypes';

const labelClass = 'block text-[15px] leading-5 font-semibold text-ink mb-[6px]';
const hintClass = 'text-small text-ink-3 mt-1';

function Field({
  title,
  children,
  wide = false,
  hint,
}: {
  title: string;
  children: ReactNode;
  wide?: boolean;
  hint?: ReactNode;
}) {
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <label className={labelClass}>{title}</label>
      {children}
      {hint ? <p className={hintClass}>{hint}</p> : null}
    </div>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${fmt(n)} ${n === 1 ? one : many}`;
const isAre = (n: number) => (n === 1 ? 'is' : 'are');
const quote = (s: string) => `“${s}”`;
const shortDay = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-CA', { timeZone: 'UTC', weekday: 'short' });

function usePrint(path: string) {
  const [error, setError] = useState<unknown>(null);
  const open = async (query = '') => {
    const tab = openBlankTab();
    try {
      const doc = (await api.get(`${path}${query}`)).data.data as Printable;
      showPrintable(tab, doc.html);
      setError(null);
    } catch (err) {
      tab?.close();
      setError(err);
    }
  };
  return { open, error };
}

function Loading({ q, title }: { q: ReturnType<typeof useDayOf>; title: string }) {
  if (q.isLoading)
    return (
      <Panel>
        <PanelHeader title={title} />
        <PanelBody>
          <SkeletonRows rows={3} />
        </PanelBody>
      </Panel>
    );
  return (
    <Panel>
      <PanelHeader title={title} />
      <PanelBody>
        <ErrorNotice>{errorMessage(q.error, 'Could not load the day-of logistics.')}</ErrorNotice>
      </PanelBody>
    </Panel>
  );
}

const time = (a?: string | null, b?: string | null) => [a, b].filter(Boolean).join('–');

const dialogFoot = (primary: ReactNode, onClose: () => void, extra?: ReactNode) => (
  <>
    {primary}
    <Button onClick={onClose}>Cancel</Button>
    {extra}
  </>
);

// ---------------------------------------------------------------- the tab's opening

/** The tab's opening sentence: what is still open for the day, most actionable first. Said once, by whichever of
 *  the tab's sections comes first on the page, with the viewer note under it. */
function DayOfLead({ eventId, d }: { eventId: string; d: DayOf }) {
  useMarkPresent(`dayof-lead:${eventId}`);
  const c = d.counts;
  const loaded = c.parking.perDay.reduce((n, day) => n + day.garages.reduce((m, g) => m + g.total, 0), 0);
  const clauses: string[] = [];
  const asked = c.parking.requested;
  if (asked) {
    const need = `${plural(asked, 'person', 'people')} ${asked === 1 ? 'needs' : 'need'} parking codes`;
    const toSend = asked - c.parking.sent;
    if (!loaded) clauses.push(`${need} and none are loaded yet.`);
    else if (toSend > 0) clauses.push(`${need} and ${fmt(toSend)} ${isAre(toSend)} still to send.`);
  }
  if (c.exceptions.pending)
    clauses.push(
      `${plural(c.exceptions.pending, 'travel exception')} ${isAre(c.exceptions.pending)} waiting for the NPI.`,
    );
  if (c.accommodations.requested)
    clauses.push(
      `${plural(c.accommodations.requested, 'accommodation')} ${isAre(c.accommodations.requested)} still to arrange.`,
    );
  return (
    <>
      <Lead
        lead={clauses.length ? clauses.slice(0, 3).join(' ') : 'Everything for the day is arranged.'}
        sub="Who works where on the day, groups and tables, parking, accommodations and travel exceptions."
      />
      {!d.visible ? (
        <div className="mt-6 mb-2">
          <InfoNotice>Names, codes and requests on this tab are shown to admins.</InfoNotice>
        </div>
      ) : null}
      <div className="h-8 max-md:h-4" />
    </>
  );
}

// ---------------------------------------------------------------- staff roster

function StationsModal({ eventId, stations, onClose }: { eventId: string; stations: Station[]; onClose: () => void }) {
  const [rows, setRows] = useState(stations.map((s) => ({ key: s.key, name: s.name, items: s.items.join('\n') })));
  const save = useDayOfWrite(eventId, () =>
    api.put(`/events/${eventId}/dayof/stations`, {
      stations: rows.map((r) => ({ key: r.key || undefined, name: r.name, items: r.items })),
    }),
  );
  return (
    <Modal
      title="Stations and items to bring"
      why="Each station’s items print on the sheet of everyone working there."
      onClose={onClose}
      width={680}
      footer={dialogFoot(
        <Button
          variant="primary"
          disabled={save.isLoading}
          onClick={() => save.mutate(undefined, { onSuccess: onClose })}
        >
          Save the stations
        </Button>,
        onClose,
      )}
    >
      {save.isError ? <ErrorNotice>{errorMessage(save.error, 'Could not save the stations.')}</ErrorNotice> : null}
      <table className="w-full">
        <thead className="max-md:hidden">
          <tr className={headRow}>
            <th className={th}>Station</th>
            <th className={th}>Items to bring, one per line</th>
            <th className={th}>
              <span className="sr-only">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-line max-md:grid max-md:grid-cols-[1fr_auto] max-md:py-2">
              <td className={`${td} max-md:col-span-1`}>
                <input
                  className={inputClass}
                  aria-label="Station"
                  placeholder="Station"
                  value={r.name}
                  onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                />
              </td>
              <td className={`${td} max-md:col-span-2 max-md:row-start-2 max-md:pt-0`}>
                <textarea
                  className={inputClass}
                  rows={2}
                  aria-label="Items to bring, one per line"
                  placeholder="Items to bring, one per line"
                  value={r.items}
                  onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, items: e.target.value } : x)))}
                />
              </td>
              <td className={`${td} w-[52px]`}>
                <Button
                  variant="icon"
                  size="sm"
                  aria-label={`Remove ${r.name || 'this station'}`}
                  onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                >
                  ×
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button
        size="sm"
        variant="quiet"
        className="mt-2 px-0"
        onClick={() => setRows((rs) => [...rs, { key: '', name: '', items: '' }])}
      >
        Add a station
      </Button>
    </Modal>
  );
}

function ShiftModal({ eventId, data, onClose }: { eventId: string; data: DayOf; onClose: () => void }) {
  const [f, setF] = useState({
    who: '',
    date: data.days[0].date,
    station: data.stations[0]?.key ?? '',
    sessionId: '',
    start: '',
    end: '',
    items: '',
    notes: '',
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const person = f.who.includes('@') ? { email: f.who.trim() } : { name: f.who.trim() };
  const save = useDayOfWrite(eventId, () =>
    api.post(`/events/${eventId}/dayof/shifts`, {
      ...person,
      date: f.date,
      station: f.station,
      sessionId: f.sessionId || null,
      start: f.start || null,
      end: f.end || null,
      items: f.items,
      notes: f.notes,
    }),
  );
  const sessions = (data.sessions ?? []).filter((s) => s.day === f.date);
  return (
    <Modal
      title="Add someone to the roster"
      why="They get a printable sheet with their times, station and what to bring."
      onClose={onClose}
      width={640}
      footer={
        <>
          {dialogFoot(
            <Button
              variant="primary"
              disabled={save.isLoading || !f.who.trim()}
              onClick={() => save.mutate(undefined, { onSuccess: onClose })}
            >
              Add to the roster
            </Button>,
            onClose,
          )}
          {!f.who.trim() ? (
            <p className="basis-full text-small text-ink-3">Add turns on once you give a name.</p>
          ) : null}
        </>
      }
    >
      {save.isError ? <ErrorNotice>{errorMessage(save.error, 'Could not add the shift.')}</ErrorNotice> : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field title="Email (a member or contact) or name" wide>
          <input className={inputClass} value={f.who} onChange={(e) => set('who', e.target.value)} />
        </Field>
        <Field title="Day">
          <select className={inputClass} value={f.date} onChange={(e) => set('date', e.target.value)}>
            {data.days.map((d) => (
              <option key={d.date} value={d.date}>
                {dayLabel(d.date)}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Station">
          <select className={inputClass} value={f.station} onChange={(e) => set('station', e.target.value)}>
            {data.stations.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Session" wide hint="Optional.">
          <select className={inputClass} value={f.sessionId} onChange={(e) => set('sessionId', e.target.value)}>
            <option value="">The whole station shift</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.startTime} {s.title}
                {s.room ? ` (${s.room})` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field title="From">
          <input type="time" className={inputClass} value={f.start} onChange={(e) => set('start', e.target.value)} />
        </Field>
        <Field title="To">
          <input type="time" className={inputClass} value={f.end} onChange={(e) => set('end', e.target.value)} />
        </Field>
        <Field title="Their own items to bring" wide hint="One per line.">
          <textarea className={inputClass} rows={2} value={f.items} onChange={(e) => set('items', e.target.value)} />
        </Field>
        <Field title="Notes" wide>
          <input className={inputClass} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

/** Staff and helpers by day, station and session, with each person's printable sheet. */
export function StaffRosterPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useDayOf(eventId);
  const [adding, setAdding] = useState(false);
  const [stations, setStations] = useState(false);
  const print = usePrint(`/events/${eventId}/dayof/staff-sheets`);
  const remove = useDayOfWrite(eventId, (id: string) => api.delete(`/events/${eventId}/dayof/shifts/${id}`));
  if (!q.data) return <Loading q={q} title="Staff and helpers" />;
  const d = q.data;
  return (
    <>
      <DayOfLead eventId={eventId} d={d} />
      <Panel>
        <PanelHeader
          title="Staff and helpers"
          note={`${plural(d.counts.staff, 'person', 'people')}, ${plural(d.counts.shifts, 'shift')}`}
        >
          {d.visible && canEdit ? <Button onClick={() => setStations(true)}>Stations</Button> : null}
          {d.visible ? (
            <Button disabled={!d.counts.shifts} onClick={() => print.open()}>
              Print everyone’s sheet
            </Button>
          ) : null}
          {d.visible && canEdit ? <Button onClick={() => setAdding(true)}>Add to the roster</Button> : null}
        </PanelHeader>
        <PanelBody>
          {print.error ? <ErrorNotice>{errorMessage(print.error, 'Could not prepare the sheets.')}</ErrorNotice> : null}
          {remove.isError ? (
            <ErrorNotice>{errorMessage(remove.error, 'Could not remove the shift.')}</ErrorNotice>
          ) : null}
          {!d.visible ? null : !d.shifts?.length ? (
            <EmptyState title="Nobody on the roster yet">
              Assign staff and helpers to the registration desk, Zoom chat per room, interpreter liaison, the nametag
              printer and the game team. Each gets a printable sheet with their times and what to bring.
            </EmptyState>
          ) : (
            d.days.map((day) => {
              const list = (d.shifts ?? [])
                .filter((s) => s.date === day.date)
                .sort((a, b) => `${a.start ?? ''}${a.station}`.localeCompare(`${b.start ?? ''}${b.station}`));
              if (!list.length) return null;
              return (
                <table key={day.date} className={`${stack.table} table-fixed mb-8 last:mb-0`}>
                  <colgroup>
                    <col className="w-[180px]" />
                    <col className="w-[26%]" />
                    <col />
                    <col className="w-[140px]" />
                  </colgroup>
                  <thead className={stack.thead}>
                    <tr className={headRow}>
                      <th className={th}>Time, {weekDay(day.date)}</th>
                      <th className={th}>Who</th>
                      <th className={th}>Station</th>
                      <th className={th}>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className={stack.tbody}>
                    {list.map((s) => {
                      const session = d.sessions?.find((x) => x.id === s.sessionId);
                      return (
                        <tr key={s.id} className={stack.tr}>
                          <td className={`${td} ${stack.td} tabular text-ink-2`}>
                            <span className="md:hidden">{weekDay(day.date)} · </span>
                            {time(s.start ?? session?.startTime, s.end ?? session?.endTime) || 'All day'}
                          </td>
                          <td className={`${td} ${stack.td} font-semibold`}>{s.personName}</td>
                          <td className={`${td} ${stack.td} ${stack.labelled}`} data-label="Station">
                            {d.stations.find((x) => x.key === s.station)?.name ?? s.station}
                            {session ? (
                              <span className={em}>
                                {session.title}
                                {session.room ? `, ${session.room}` : ''}
                              </span>
                            ) : null}
                            {s.notes ? <span className={em}>{s.notes}</span> : null}
                          </td>
                          <td className={`${td} ${stack.td} ${stack.acts}`}>
                            <span className="inline-flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => print.open(`?person=${encodeURIComponent(s.personKey)}`)}
                              >
                                Sheet
                              </Button>
                              {canEdit ? (
                                <MoreMenu
                                  label={`More for ${s.personName}’s shift`}
                                  items={[
                                    {
                                      label: 'Remove this shift',
                                      destructive: true,
                                      onClick: () => {
                                        void confirmAction(
                                          `Remove ${s.personName}’s shift? Their sheet no longer lists it.`,
                                          'Remove the shift',
                                        ).then((ok) => ok && remove.mutate(s.id));
                                      },
                                    },
                                  ]}
                                />
                              ) : null}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })
          )}
        </PanelBody>
        {adding ? <ShiftModal eventId={eventId} data={d} onClose={() => setAdding(false)} /> : null}
        {stations ? <StationsModal eventId={eventId} stations={d.stations} onClose={() => setStations(false)} /> : null}
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------- groups

function GroupsModal({ eventId, d, onClose }: { eventId: string; d: DayOf; onClose: () => void }) {
  const [names, setNames] = useState(d.groups.names.length ? d.groups.names.join('\n') : '');
  const [count, setCount] = useState(String(d.groups.names.length || 6));
  const [preview, setPreview] = useState<{ sizes: { name: string; count: number }[]; changes: number } | null>(null);
  const body = () => {
    const list = names
      .split('\n')
      .map((n) => n.trim())
      .filter(Boolean);
    return list.length ? { names: list } : { count: Number(count) };
  };
  const check = useDayOfWrite(eventId, () =>
    api.post(`/events/${eventId}/dayof/groups`, { ...body(), dryRun: true }).then((r) => setPreview(r.data.data)),
  );
  const assign = useDayOfWrite(eventId, (reassign: boolean) =>
    api.post(`/events/${eventId}/dayof/groups`, { ...body(), reassign, dryRun: false }).then(() => setPreview(null)),
  );
  const has = d.groups.names.length > 0;
  return (
    <Modal
      title={has ? 'Change the groups' : 'Make the groups'}
      why="Groups are balanced by audience and spread people from the same organization; the group prints on the nametag (Canva file and printable nametags). Preview first: nothing is saved until you assign."
      onClose={onClose}
      width={620}
      footer={
        <>
          {dialogFoot(
            <Button
              variant="primary"
              disabled={assign.isLoading || !preview?.changes}
              onClick={() => assign.mutate(false, { onSuccess: onClose })}
            >
              {preview?.changes ? `Assign (${plural(preview.changes, 'change')})` : 'Assign'}
            </Button>,
            onClose,
            has ? (
              <Button
                className="ml-auto max-md:ml-0"
                disabled={assign.isLoading}
                onClick={() => {
                  void confirmAction(
                    'Reshuffle everyone, including people already in a group? Nametags already printed will be out of date.',
                    'Reshuffle everyone',
                  ).then((ok) => ok && assign.mutate(true, { onSuccess: onClose }));
                }}
              >
                Reshuffle everyone
              </Button>
            ) : null,
          )}
          {!preview ? (
            <p className="basis-full text-small text-ink-3">Assign turns on after a preview that moves someone.</p>
          ) : null}
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field title="Number of groups">
          <input
            className={inputClass}
            inputMode="numeric"
            value={count}
            onChange={(e) => {
              setCount(e.target.value);
              setPreview(null);
            }}
          />
        </Field>
        <Field title="Or their names" hint="One per line, for example mentorship groups.">
          <textarea
            className={inputClass}
            rows={3}
            value={names}
            onChange={(e) => {
              setNames(e.target.value);
              setPreview(null);
            }}
          />
        </Field>
      </div>
      <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <Button size="sm" disabled={check.isLoading} onClick={() => check.mutate(undefined)}>
          Preview
        </Button>
        {preview ? (
          <p className="text-ink flex-[1_1_300px]">
            {preview.changes
              ? `Not saved yet: ${plural(preview.changes, 'person', 'people')} would be placed or moved.`
              : 'Nobody would move. These are the groups as they stand.'}
            <span className={em}>{preview.sizes.map((g) => `${g.name}: ${g.count}`).join(' · ')}</span>
          </p>
        ) : null}
      </div>
      {check.isError || assign.isError ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(check.error || assign.error, 'Could not assign the groups.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function groupSentence(sizes: { name: string; count: number }[]) {
  if (!sizes.length) return '';
  const same = sizes.every((g) => g.count === sizes[0].count);
  const range = sizes.length > 1 ? `${sizes[0].name} to ${sizes[sizes.length - 1].name}` : sizes[0].name;
  return same
    ? `${plural(sizes.length, 'group')} of ${fmt(sizes[0].count)}: ${range}.`
    : `${plural(sizes.length, 'group')}: ${sizes.map((g) => `${g.name} (${fmt(g.count)})`).join(', ')}.`;
}

/** Balanced groups or tables, printed on the nametag and the group list. */
export function GroupsPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useDayOf(eventId);
  const leads = !usePanelShown(eventId, 'before.logistics.staff');
  const [changing, setChanging] = useState(false);
  const [show, setShow] = useState(false);
  const print = usePrint(`/events/${eventId}/dayof/group-list`);
  const setOne = useDayOfWrite(eventId, ({ id, group }: { id: string; group: string }) =>
    api.patch(`/events/invitees/${id}/group`, { group: group || null }),
  );
  if (!q.data) return <Loading q={q} title="Groups and tables" />;
  const d = q.data;
  const has = d.groups.names.length > 0;
  const placed = d.counts.grouped;
  const total = d.counts.expected;
  return (
    <>
      {leads ? <DayOfLead eventId={eventId} d={d} /> : null}
      <Panel>
        <PanelHeader
          title="Groups and tables"
          note={
            has
              ? placed === total
                ? `all ${fmt(total)} in a group`
                : `${fmt(placed)} of ${fmt(total)} in a group`
              : undefined
          }
        >
          {d.visible && has ? <Button onClick={() => print.open()}>Print the group list</Button> : null}
          {d.visible && canEdit ? (
            <Button onClick={() => setChanging(true)}>{has ? 'Change the groups' : 'Make the groups'}</Button>
          ) : null}
        </PanelHeader>
        <PanelBody>
          {print.error ? <ErrorNotice>{errorMessage(print.error, 'Could not prepare the list.')}</ErrorNotice> : null}
          <p className="text-[15px] leading-[22px] text-ink max-w-[72ch]">
            {has
              ? `${groupSentence(d.groups.sizes)} Groups are balanced by audience, spread people from the same organization, and print on the nametag.`
              : 'No groups yet. Groups are balanced by audience and spread people from the same organization; the group prints on the nametag (Canva file and printable nametags).'}
          </p>
          {d.visible && has ? (
            <>
              <Button
                size="sm"
                variant="quiet"
                className="mt-2 px-0"
                aria-expanded={show}
                onClick={() => setShow((v) => !v)}
              >
                {show ? 'Hide the people' : 'Move someone to another group'}
              </Button>
              {setOne.isError ? <ErrorNotice>{errorMessage(setOne.error, 'Could not move them.')}</ErrorNotice> : null}
              {show ? (
                <table className={`${stack.table} mt-3`}>
                  <thead className={stack.thead}>
                    <tr className={headRow}>
                      <th className={th}>Person</th>
                      <th className={th}>Organization</th>
                      <th className={`${th} w-[240px]`}>Group</th>
                    </tr>
                  </thead>
                  <tbody className={stack.tbody}>
                    {(d.people ?? []).map((p) => (
                      <tr key={p.inviteeId} className={stack.tr}>
                        <td className={`${td} ${stack.td} font-semibold`}>{p.name}</td>
                        <td className={`${td} ${stack.td} ${stack.labelled} text-ink-2`} data-label="Organization">
                          {p.organization || <span className="text-ink-3">–</span>}
                        </td>
                        <td className={`${td} ${stack.td} py-[4px]`}>
                          <select
                            className={inputClass}
                            aria-label={`Group for ${p.name}`}
                            disabled={!canEdit}
                            value={p.group ?? ''}
                            onChange={(e) => setOne.mutate({ id: p.inviteeId, group: e.target.value })}
                          >
                            <option value="">No group</option>
                            {d.groups.names.map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </>
          ) : null}
        </PanelBody>
        {changing ? <GroupsModal eventId={eventId} d={d} onClose={() => setChanging(false)} /> : null}
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------- parking

function ParkingMessageModal({
  eventId,
  inviteeId,
  onClose,
}: {
  eventId: string;
  inviteeId: string;
  onClose: () => void;
}) {
  const msg = useQuery(
    ['event-dayof-parking-msg', eventId, inviteeId],
    async () =>
      (await api.get(`/events/${eventId}/dayof/parking/message/${inviteeId}`)).data.data as {
        to: string | null;
        subject: string;
        text: string;
      },
  );
  const sent = useDayOfWrite(eventId, () => api.post(`/events/${eventId}/dayof/parking/sent`, { inviteeId }));
  return (
    <Modal
      title="Parking codes message"
      why="The platform never sends this. Copy it into your email, send it, then press “I sent it”."
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button
            variant="primary"
            disabled={sent.isLoading || !msg.data}
            onClick={() => sent.mutate(undefined, { onSuccess: onClose })}
          >
            I sent it
          </Button>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {msg.isLoading ? (
        <SkeletonRows rows={4} />
      ) : msg.isError ? (
        <ErrorNotice>{errorMessage(msg.error, 'Could not prepare the message.')}</ErrorNotice>
      ) : msg.data ? (
        <>
          <dl className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1">
            <dt className="text-ink-3">To</dt>
            <dd className="text-ink break-words">{msg.data.to ?? 'no email on file'}</dd>
            <dt className="text-ink-3">Subject</dt>
            <dd className="text-ink">{msg.data.subject}</dd>
          </dl>
          <label className={`${labelClass} mt-4`} htmlFor="parking-message">
            Message
          </label>
          <textarea
            id="parking-message"
            readOnly
            className={`${inputClass} text-small`}
            rows={14}
            value={msg.data.text}
            onFocus={(e) => e.target.select()}
          />
        </>
      ) : null}
    </Modal>
  );
}

function GaragesModal({ eventId, d, onClose }: { eventId: string; d: DayOf; onClose: () => void }) {
  const garages = d.parking?.garages ?? [];
  const [text, setText] = useState(garages.map((g) => `${g.name} | ${g.instructions ?? ''}`).join('\n'));
  const save = useDayOfWrite(eventId, (list: { name: string; instructions: string }[]) =>
    api.put(`/events/${eventId}/dayof/parking/garages`, { garages: list }),
  );
  return (
    <Modal
      title={garages.length ? 'Edit the garages' : 'Add the garages'}
      why="Each garage’s instructions go into the parking message people receive with their codes."
      onClose={onClose}
      width={620}
      footer={dialogFoot(
        <Button
          variant="primary"
          disabled={save.isLoading}
          onClick={() =>
            save.mutate(
              text
                .split('\n')
                .map((l) => l.split('|'))
                .filter((p) => p[0].trim())
                .map((p) => ({ name: p[0].trim(), instructions: (p[1] ?? '').trim() })),
              { onSuccess: onClose },
            )
          }
        >
          Save the garages
        </Button>,
        onClose,
      )}
    >
      {save.isError ? <ErrorNotice>{errorMessage(save.error, 'Could not save the garages.')}</ErrorNotice> : null}
      <Field title="Garages" hint="One garage per line: name | how to use the code (HONK zone, entrance).">
        <textarea className={inputClass} rows={4} value={text} onChange={(e) => setText(e.target.value)} />
      </Field>
    </Modal>
  );
}

function CodesModal({ eventId, d, onClose }: { eventId: string; d: DayOf; onClose: () => void }) {
  const garages = d.parking?.garages ?? [];
  const [paste, setPaste] = useState({
    garage: garages.length === 1 ? garages[0].key : '',
    date: d.days.length === 1 ? d.days[0].date : '',
    codes: '',
  });
  const add = useDayOfWrite(eventId, () => api.post(`/events/${eventId}/dayof/parking/codes`, paste));
  const ready = paste.garage && paste.date && paste.codes.trim();
  return (
    <Modal
      title="Add parking codes"
      why="Paste the codes for one garage and one day. Assign free codes then gives each person theirs."
      onClose={onClose}
      width={620}
      footer={
        <>
          {dialogFoot(
            <Button
              variant="primary"
              disabled={add.isLoading || !ready}
              onClick={() => add.mutate(undefined, { onSuccess: onClose })}
            >
              Add the codes
            </Button>,
            onClose,
          )}
          {!ready ? (
            <p className="basis-full text-small text-ink-3">Add turns on once a garage, a day and codes are given.</p>
          ) : null}
        </>
      }
    >
      {add.isError ? <ErrorNotice>{errorMessage(add.error, 'Could not save the codes.')}</ErrorNotice> : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field title="Garage">
          <select
            className={inputClass}
            value={paste.garage}
            onChange={(e) => setPaste((p) => ({ ...p, garage: e.target.value }))}
          >
            <option value="">Choose a garage</option>
            {garages.map((g) => (
              <option key={g.key} value={g.key}>
                {g.name}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Day">
          <select
            className={inputClass}
            value={paste.date}
            onChange={(e) => setPaste((p) => ({ ...p, date: e.target.value }))}
          >
            <option value="">Choose a day</option>
            {d.days.map((x) => (
              <option key={x.date} value={x.date}>
                {dayLabel(x.date)}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Codes" wide hint="Paste them, one per line.">
          <textarea
            className={inputClass}
            rows={5}
            value={paste.codes}
            onChange={(e) => setPaste((p) => ({ ...p, codes: e.target.value }))}
          />
        </Field>
      </div>
    </Modal>
  );
}

/** Parking codes per garage and day: paste them in, assign per person per day, send, see what is left. */
export function ParkingPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useDayOf(eventId);
  const staffShown = usePanelShown(eventId, 'before.logistics.staff');
  const groupsShown = usePanelShown(eventId, 'before.logistics.groups');
  const [dialog, setDialog] = useState<'garages' | 'codes' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const assign = useDayOfWrite(eventId, () => api.post(`/events/${eventId}/dayof/parking/assign`, { dryRun: false }));
  const voidCode = useDayOfWrite(eventId, (id: string) =>
    api.patch(`/events/${eventId}/dayof/parking/codes/${id}`, { void: true }),
  );
  if (!q.data) return <Loading q={q} title="Parking codes" />;
  const d = q.data;
  const c = d.counts.parking;
  const garages = d.parking?.garages ?? [];
  const columns = d.garages.length ? d.garages : garages;
  const people = (d.people ?? []).filter((p) => p.parking);
  const codes = d.parking?.codes ?? [];
  const loaded = c.perDay.reduce((n, day) => n + day.garages.reduce((m, g) => m + g.total, 0), 0);
  const addFirst = c.requested > 0 && !loaded;
  const edit = d.visible && canEdit;
  return (
    <>
      {!staffShown && !groupsShown ? <DayOfLead eventId={eventId} d={d} /> : null}
      <Panel>
        <PanelHeader
          title="Parking codes"
          note={`${fmt(c.requested)} asked · ${fmt(c.withCodes)} with codes · ${fmt(c.sent)} sent`}
        >
          {edit ? (
            <Button onClick={() => setDialog('garages')}>
              {garages.length ? 'Edit the garages' : 'Add the garages'}
            </Button>
          ) : null}
          {edit && garages.length ? (
            <Button variant={addFirst ? 'primary' : 'secondary'} onClick={() => setDialog('codes')}>
              Add codes
            </Button>
          ) : null}
          {edit && garages.length ? (
            <Button
              variant={!addFirst && loaded ? 'primary' : 'secondary'}
              disabled={assign.isLoading}
              onClick={() => assign.mutate(undefined)}
            >
              Assign free codes
            </Button>
          ) : null}
        </PanelHeader>
        <PanelBody>
          {assign.isError || voidCode.isError ? (
            <div className="mb-3">
              <ErrorNotice>{errorMessage(assign.error || voidCode.error, 'Could not save the codes.')}</ErrorNotice>
            </div>
          ) : null}
          <div className={`grid gap-x-12 gap-y-8 ${d.visible && people.length ? 'lg:grid-cols-2' : ''}`}>
            <div className="min-w-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={headRow}>
                      <th className={th}>Day</th>
                      <th className={`${th} ${num}`}>Needed</th>
                      {columns.map((g) => (
                        <th key={g.key} className={th}>
                          Garage “{g.name}”
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {c.perDay.map((day) => {
                      const short = day.needed - day.garages.reduce((n, g) => n + g.assigned, 0);
                      return (
                        <tr key={day.date} className="border-b border-line hover:bg-selected">
                          <td className={`${td} whitespace-nowrap`}>{weekDay(day.date)}</td>
                          <td className={`${td} ${num}`}>{fmt(day.needed)}</td>
                          {columns.map((col) => {
                            const g = day.garages.find((x) => x.key === col.key);
                            return (
                              <td key={col.key} className={td}>
                                {!g || !g.total ? (
                                  <span className="text-ink-3">No codes loaded</span>
                                ) : (
                                  <>
                                    <span className="tabular">
                                      {fmt(g.assigned)} of {fmt(g.total)} used, {fmt(g.left)} left
                                    </span>
                                    {g.left === 0 && short > 0 ? (
                                      <span className="block">
                                        <Pill tone="problem" small>{`${fmt(short)} short`}</Pill>
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-small text-ink-3 mt-3 max-w-[72ch]">
                {!garages.length
                  ? 'Add the garages first, then paste their codes per day.'
                  : loaded
                    ? `${plural(c.left, 'code')} left in all.`
                    : 'Paste codes per garage and day with Add codes, then Assign free codes gives each person theirs.'}
              </p>
            </div>
            {d.visible && people.length ? (
              <div className="min-w-0">
                <table className={stack.table}>
                  <thead className={stack.thead}>
                    <tr className={headRow}>
                      <th className={th}>Person</th>
                      <th className={th}>Codes</th>
                      <th className={th}>Sent</th>
                      <th className={th}>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className={stack.tbody}>
                    {people.map((p) => {
                      const mine = codes.filter((x) => x.inviteeId === p.inviteeId && !x.void);
                      const sent = mine.length > 0 && mine.every((x) => x.sentAt);
                      const allDays = p.days.length > 1 && p.days.length === d.days.length;
                      const codesText = mine.length
                        ? p.days
                            .map((day) => `${shortDay(day)}: ${mine.find((x) => x.date === day)?.code ?? '–'}`)
                            .join(' · ')
                        : allDays
                          ? d.days.length === 2
                            ? 'Both days'
                            : `All ${d.days.length} days`
                          : p.days.map(shortDay).join(', ') || '–';
                      return (
                        <tr key={p.inviteeId} className={stack.tr}>
                          <td className={`${td} ${stack.td} font-semibold`}>{p.name}</td>
                          <td
                            className={`${td} ${stack.td} ${stack.labelled} ${mine.length ? 'tabular' : 'text-ink-2'}`}
                            data-label="Codes"
                          >
                            {codesText}
                          </td>
                          <td className={`${td} ${stack.td} ${stack.labelled}`} data-label="Sent">
                            {sent ? (
                              <Pill tone="done" small>
                                Sent
                              </Pill>
                            ) : mine.length ? (
                              <Pill tone="running" small>
                                Not sent
                              </Pill>
                            ) : (
                              <span className="text-ink-3">–</span>
                            )}
                          </td>
                          <td className={`${td} ${stack.td} ${stack.acts}`}>
                            {canEdit && mine.length ? (
                              <span className="inline-flex gap-2">
                                <Button size="sm" onClick={() => setMessage(p.inviteeId)}>
                                  {sent ? 'Message again' : 'Prepare the message'}
                                </Button>
                                <MoreMenu
                                  label={`More for ${p.name}’s codes`}
                                  items={mine.map((x) => ({
                                    label: `Void ${x.code} (${shortDay(x.date)})`,
                                    destructive: true,
                                    onClick: () => {
                                      void confirmAction(
                                        `Void ${x.code}? Use this when the code was lost or does not work, then assign a new one.`,
                                        'Void the code',
                                      ).then((ok) => ok && voidCode.mutate(x.id));
                                    },
                                  }))}
                                />
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </PanelBody>
        {dialog === 'garages' ? <GaragesModal eventId={eventId} d={d} onClose={() => setDialog(null)} /> : null}
        {dialog === 'codes' ? <CodesModal eventId={eventId} d={d} onClose={() => setDialog(null)} /> : null}
        {message ? (
          <ParkingMessageModal eventId={eventId} inviteeId={message} onClose={() => setMessage(null)} />
        ) : null}
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------- arrivals and departures

const MODES = ['flight', 'train', 'bus', 'car', 'other'];

function LegInputs({ label, leg, onChange }: { label: string; leg: Leg; onChange: (l: Leg) => void }) {
  return (
    <fieldset>
      <legend className="font-serif font-semibold text-[17px] leading-6 text-ink mb-2">{label}</legend>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <Field title="Day">
          <input
            type="date"
            className={inputClass}
            value={leg.date ?? ''}
            onChange={(e) => onChange({ ...leg, date: e.target.value })}
          />
        </Field>
        <Field title="Time">
          <input
            type="time"
            className={inputClass}
            value={leg.time ?? ''}
            onChange={(e) => onChange({ ...leg, time: e.target.value })}
          />
        </Field>
        <Field title="Airport or station">
          <input
            className={inputClass}
            placeholder="YYZ"
            value={leg.via ?? ''}
            onChange={(e) => onChange({ ...leg, via: e.target.value })}
          />
        </Field>
        <Field title="By">
          <select
            className={inputClass}
            value={leg.mode ?? ''}
            onChange={(e) => onChange({ ...leg, mode: e.target.value })}
          >
            <option value="" />
            {MODES.map((m) => (
              <option key={m} value={m}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Flight or train">
          <input
            className={inputClass}
            value={leg.ref ?? ''}
            onChange={(e) => onChange({ ...leg, ref: e.target.value })}
          />
        </Field>
      </div>
    </fieldset>
  );
}

function TravelModal({
  eventId,
  person,
  onClose,
}: {
  eventId: string;
  person: { inviteeId: string; name: string; travel: { arrive?: Leg; depart?: Leg } | null };
  onClose: () => void;
}) {
  const [arrive, setArrive] = useState<Leg>(person.travel?.arrive ?? {});
  const [depart, setDepart] = useState<Leg>(person.travel?.depart ?? {});
  const clean = (l: Leg) =>
    Object.values(l).some(Boolean) ? Object.fromEntries(Object.entries(l).filter(([, v]) => v)) : null;
  const save = useDayOfWrite(eventId, () =>
    api.patch(`/events/invitees/${person.inviteeId}/travel`, { arrive: clean(arrive), depart: clean(depart) }),
  );
  return (
    <Modal
      title={`Travel: ${person.name}`}
      why="Arrivals and departures at the same place and time are suggested as shared rides."
      onClose={onClose}
      width={760}
      footer={dialogFoot(
        <Button
          variant="primary"
          disabled={save.isLoading}
          onClick={() => save.mutate(undefined, { onSuccess: onClose })}
        >
          Save
        </Button>,
        onClose,
      )}
    >
      {save.isError ? <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice> : null}
      <LegInputs label="Arrival" leg={arrive} onChange={setArrive} />
      <div className="mt-5">
        <LegInputs label="Departure" leg={depart} onChange={setDepart} />
      </div>
    </Modal>
  );
}

function BoardTable({ title, days, nameOf }: { title: string; days: BoardDay[]; nameOf: (k: string) => string }) {
  return (
    <div className="mt-6 first:mt-0">
      <h3 className="font-serif font-semibold text-[17px] leading-6 text-ink mb-2">{title}</h3>
      {!days.length ? (
        <p className="text-[15px] leading-[22px] text-ink-2">None recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] table-fixed">
            <colgroup>
              <col className="w-[96px]" />
              <col className="w-[180px]" />
              <col />
              <col className="w-[180px]" />
              <col className="w-[140px]" />
            </colgroup>
            <thead>
              <tr className={headRow}>
                <th className={th}>Time</th>
                <th className={th}>Airport or station</th>
                <th className={th}>Person</th>
                <th className={th}>Flight or train</th>
                <th className={th}>Shared ride</th>
              </tr>
            </thead>
            {days.map((d) => (
              <tbody key={d.date}>
                <tr>
                  <th colSpan={5} scope="colgroup" className="pt-4 pb-1 text-left text-[15px] font-semibold text-ink">
                    {weekDay(d.date)} <span className="font-normal text-ink-3">· {fmt(d.count)}</span>
                  </th>
                </tr>
                {d.places.flatMap((pl) => {
                  const rideOf = new Map<string, number>();
                  pl.rides.forEach((r, i) => r.forEach((k) => rideOf.set(k, i + 1)));
                  return pl.people.map((p) => (
                    <tr key={pl.via + p.key + p.date} className="border-b border-line hover:bg-selected">
                      <td className={`${td} tabular`}>{p.time ?? <span className="text-ink-3">–</span>}</td>
                      <td className={td}>{pl.via}</td>
                      <td className={`${td} font-semibold`}>{nameOf(p.key) || p.name}</td>
                      <td className={td}>{p.ref ?? <span className="text-ink-3">–</span>}</td>
                      <td className={td}>
                        {rideOf.get(p.key) ? `Ride ${rideOf.get(p.key)}` : <span className="text-ink-3">–</span>}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </div>
  );
}

/** Arrivals and departures by airport or station and time, with shared-ride suggestions. */
export function ArrivalsPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useDayOf(eventId);
  const staffShown = usePanelShown(eventId, 'before.logistics.staff');
  const groupsShown = usePanelShown(eventId, 'before.logistics.groups');
  const parkingShown = usePanelShown(eventId, 'before.logistics.parking');
  const leads = !staffShown && !groupsShown && !parkingShown;
  const [editing, setEditing] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const [opened, setOpened] = useState(false);
  const print = usePrint(`/events/${eventId}/dayof/travel-list`);
  if (!q.data) return <Loading q={q} title="Arrivals and departures" />;
  const d = q.data;
  const lead = leads ? <DayOfLead eventId={eventId} d={d} /> : null;
  const people = d.people ?? [];
  const nameOf = (k: string) => people.find((p) => p.inviteeId === k)?.name ?? '';
  const person = people.find((p) => p.inviteeId === editing);
  const hasTravel = d.counts.arrivals > 0 || Boolean(d.travel?.departures.length);
  if (!hasTravel && !opened)
    return (
      <>
        {lead}
        {canEdit && d.visible ? <AddLink label="Arrivals and departures" onClick={() => setOpened(true)} /> : null}
      </>
    );
  return (
    <>
      {lead}
      <Panel>
        <PanelHeader
          title="Arrivals and departures"
          note={`${plural(d.counts.arrivals, 'arrival')} · ${fmt(d.counts.missingArrival)} travelling with no arrival yet`}
        >
          {d.visible ? <Button onClick={() => print.open()}>Print the list</Button> : null}
          {d.visible && canEdit ? (
            <span className="inline-flex flex-nowrap gap-2 max-md:w-full">
              <select
                className={`${inputClass} w-auto max-w-[320px] max-md:max-w-none max-md:flex-1`}
                aria-label="Whose arrival and departure to add or change"
                value={pick}
                onChange={(e) => setPick(e.target.value)}
              >
                <option value="">Add or change someone’s travel…</option>
                {people.map((p) => (
                  <option key={p.inviteeId} value={p.inviteeId}>
                    {p.name}
                    {d.travel?.missingArrival.includes(p.inviteeId) ? ' (no arrival yet)' : ''}
                  </option>
                ))}
              </select>
              <Button disabled={!pick} onClick={() => setEditing(pick)}>
                Edit
              </Button>
            </span>
          ) : null}
        </PanelHeader>
        <PanelBody>
          {print.error ? <ErrorNotice>{errorMessage(print.error, 'Could not prepare the list.')}</ErrorNotice> : null}
          {d.visible && d.travel ? (
            <>
              <BoardTable title="Arrivals" days={d.travel.arrivals} nameOf={nameOf} />
              <BoardTable title="Departures" days={d.travel.departures} nameOf={nameOf} />
            </>
          ) : null}
        </PanelBody>
        {person ? <TravelModal eventId={eventId} person={person} onClose={() => setEditing(null)} /> : null}
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------- accommodations

interface FormRequest {
  inviteeId: string;
  text: string;
  list: 'accommodation' | 'exception';
}

function AccommodationModal({
  eventId,
  data,
  from,
  onClose,
}: {
  eventId: string;
  data: DayOf;
  from?: FormRequest | null;
  onClose: () => void;
}) {
  const [f, setF] = useState({
    kind: from ? 'other' : 'prayer_space',
    inviteeId: from?.inviteeId ?? '',
    where: '',
    detail: from?.text ?? '',
    days: [] as string[],
  });
  const save = useDayOfWrite(eventId, () =>
    api.post(`/events/${eventId}/dayof/accommodations`, {
      ...f,
      inviteeId: f.inviteeId || null,
      where: f.where || undefined,
    }),
  );
  return (
    <Modal
      title="Add an accommodation"
      why="What the venue or hotel must have ready. It goes on the printable list for the venue or the hotel."
      onClose={onClose}
      width={620}
      footer={dialogFoot(
        <Button
          variant="primary"
          disabled={save.isLoading}
          onClick={() => save.mutate(undefined, { onSuccess: onClose })}
        >
          Add the accommodation
        </Button>,
        onClose,
      )}
    >
      {save.isError ? <ErrorNotice>{errorMessage(save.error, 'Could not add it.')}</ErrorNotice> : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field title="What">
          <select className={inputClass} value={f.kind} onChange={(e) => setF((x) => ({ ...x, kind: e.target.value }))}>
            {data.kinds.accommodations.map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Where">
          <select
            className={inputClass}
            value={f.where}
            onChange={(e) => setF((x) => ({ ...x, where: e.target.value }))}
          >
            <option value="">Usual place</option>
            <option value="venue">Venue</option>
            <option value="hotel">Hotel</option>
          </select>
        </Field>
        <Field title="For" wide>
          <select
            className={inputClass}
            value={f.inviteeId}
            onChange={(e) => setF((x) => ({ ...x, inviteeId: e.target.value }))}
          >
            <option value="">All attendees</option>
            {(data.people ?? []).map((p) => (
              <option key={p.inviteeId} value={p.inviteeId}>
                {p.name}
                {p.accessibility ? ` — “${p.accessibility.slice(0, 50)}”` : ''}
              </option>
            ))}
          </select>
        </Field>
        <fieldset className="md:col-span-2">
          <legend className={labelClass}>Days</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {data.days.map((d) => (
              <label key={d.date} className="inline-flex gap-2 items-center min-h-[44px] text-ink">
                <input
                  type="checkbox"
                  className="w-[18px] h-[18px] accent-accent"
                  checked={f.days.includes(d.date)}
                  onChange={(e) =>
                    setF((x) => ({
                      ...x,
                      days: e.target.checked ? [...x.days, d.date] : x.days.filter((y) => y !== d.date),
                    }))
                  }
                />
                {dayLabel(d.date)}
              </label>
            ))}
          </div>
          <p className={hintClass}>None ticked: every day.</p>
        </fieldset>
        <Field title="Details" wide>
          <input
            className={inputClass}
            value={f.detail}
            onChange={(e) => setF((x) => ({ ...x, detail: e.target.value }))}
          />
        </Field>
      </div>
    </Modal>
  );
}

const ACC_TONE: Record<AccStatus, 'running' | 'healthy' | 'inactive'> = {
  requested: 'running',
  arranged: 'healthy',
  not_possible: 'inactive',
};

/** Prayer space, adjoining rooms, cribs, live captioning, dietary indicators: what the venue and hotel must have ready. */
export function AccommodationsPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useDayOf(eventId);
  const [adding, setAdding] = useState(false);
  const [from, setFrom] = useState<FormRequest | null>(null);
  const print = usePrint(`/events/${eventId}/dayof/accommodation-list`);
  const update = useDayOfWrite(eventId, ({ id, body }: { id: string; body: Record<string, unknown> }) =>
    api.patch(`/events/${eventId}/dayof/accommodations/${id}`, body),
  );
  if (!q.data) return <Loading q={q} title="Accommodations" />;
  const d = q.data;
  const label = (k: string) => d.kinds.accommodations.find((x) => x.key === k)?.label ?? k;
  const list = d.accommodations ?? [];
  const fromForms = (d.people ?? []).filter(
    (p) =>
      p.accessibility &&
      !list.some((a) => a.inviteeId === p.inviteeId) &&
      !(d.exceptions ?? []).some((x) => x.inviteeId === p.inviteeId),
  );
  const setStatus = async (a: Accommodation, status: AccStatus) => {
    if (status === 'requested') {
      update.mutate({ id: a.id, body: { status, outcome: null } });
      return;
    }
    const outcome = await promptAction(
      status === 'arranged' ? 'How was it arranged? (optional)' : 'Why not, and what instead? (optional)',
    );
    if (outcome !== null) update.mutate({ id: a.id, body: { status, outcome } });
  };
  const where = (a: Accommodation) =>
    `${a.where === 'hotel' ? 'Hotel' : 'Venue'} · ${a.days?.length ? a.days.map(weekDay).join(' and ') : 'every day'}`;
  return (
    <Panel>
      <PanelHeader
        title="Accommodations"
        note={`${fmt(d.counts.accommodations.requested)} to arrange · ${fmt(d.counts.accommodations.arranged)} arranged`}
      >
        {d.visible ? <Button onClick={() => print.open('?where=venue')}>For the venue</Button> : null}
        {d.visible ? <Button onClick={() => print.open('?where=hotel')}>For the hotel</Button> : null}
        {d.visible && canEdit ? <Button onClick={() => setAdding(true)}>Add</Button> : null}
      </PanelHeader>
      <PanelBody>
        {print.error || update.isError ? (
          <ErrorNotice>{errorMessage(print.error || update.error, 'Could not do that.')}</ErrorNotice>
        ) : null}
        {!d.visible ? null : (
          <>
            {!list.length ? (
              <EmptyState title="Nothing to arrange yet">
                Add a prayer space, adjoining rooms or a crib at the hotel, live captioning on screen for everyone, and
                dietary indicators on nametags and place cards.
              </EmptyState>
            ) : (
              <table className={stack.table}>
                <thead className={stack.thead}>
                  <tr className={headRow}>
                    <th className={`${th} w-[160px]`}>Status</th>
                    <th className={th}>What</th>
                    <th className={th}>For</th>
                    <th className={th}>Where and when</th>
                    <th className={`${th} w-[150px]`}>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={stack.tbody}>
                  {list.map((a) => {
                    const more = [
                      ...(a.status !== 'not_possible'
                        ? [{ label: 'Not possible', onClick: () => void setStatus(a, 'not_possible') }]
                        : []),
                      ...(a.status !== 'requested'
                        ? [{ label: 'Mark as to arrange again', onClick: () => void setStatus(a, 'requested') }]
                        : []),
                      {
                        label: 'Remove',
                        destructive: true,
                        onClick: () => {
                          void confirmAction(`Remove ${label(a.kind).toLowerCase()} from the list?`, 'Remove it').then(
                            (ok) => ok && update.mutate({ id: a.id, body: { remove: true } }),
                          );
                        },
                      },
                    ];
                    return (
                      <tr key={a.id} className={stack.tr}>
                        <td className={`${td} ${stack.td}`}>
                          <Pill tone={ACC_TONE[a.status]}>{STATUS_LABEL[a.status]}</Pill>
                        </td>
                        <td className={`${td} ${stack.td} font-semibold`}>
                          {label(a.kind)}
                          {a.detail ? <span className={`${em} font-normal`}>{a.detail}</span> : null}
                          {a.outcome ? <span className={`${em} font-normal`}>{a.outcome}</span> : null}
                        </td>
                        <td className={`${td} ${stack.td} ${stack.labelled}`} data-label="For">
                          {a.name ?? 'All attendees'}
                        </td>
                        <td className={`${td} ${stack.td} ${stack.labelled}`} data-label="Where">
                          {where(a)}
                        </td>
                        <td className={`${td} ${stack.td} ${stack.acts}`}>
                          {canEdit ? (
                            <span className="inline-flex gap-2">
                              {a.status !== 'arranged' ? (
                                <Button size="sm" onClick={() => void setStatus(a, 'arranged')}>
                                  Arranged
                                </Button>
                              ) : null}
                              <MoreMenu label={`More for ${label(a.kind).toLowerCase()}`} items={more} />
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {fromForms.length ? (
              <>
                <h3 className="font-serif font-semibold text-[17px] leading-6 text-ink mt-8 mb-2">
                  From the forms, not on this list yet{' '}
                  <span className="font-sans font-normal text-ink-3">· {fmt(fromForms.length)}</span>
                </h3>
                <table className={`${stack.table} md:table-fixed`}>
                  <colgroup>
                    <col className="w-[22%]" />
                    <col />
                    <col className={canEdit ? 'w-[340px]' : 'w-0'} />
                  </colgroup>
                  <thead className={stack.thead}>
                    <tr className={headRow}>
                      <th className={th}>Person</th>
                      <th className={th}>What they wrote</th>
                      <th className={th}>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className={stack.tbody}>
                    {fromForms.map((p) => (
                      <tr key={p.inviteeId} className={stack.tr}>
                        <td className={`${td} ${stack.td} font-semibold`}>{p.name}</td>
                        <td className={`${td} ${stack.td} text-ink-2`}>{quote(p.accessibility ?? '')}</td>
                        <td className={`${td} ${stack.td} ${stack.acts}`}>
                          {canEdit ? (
                            <span className="inline-flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                onClick={() =>
                                  setFrom({
                                    inviteeId: p.inviteeId,
                                    text: p.accessibility ?? '',
                                    list: 'accommodation',
                                  })
                                }
                              >
                                Add as accommodation
                              </Button>
                              <Button
                                size="sm"
                                variant="quiet"
                                onClick={() =>
                                  setFrom({ inviteeId: p.inviteeId, text: p.accessibility ?? '', list: 'exception' })
                                }
                              >
                                Travel exception
                              </Button>
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}
          </>
        )}
      </PanelBody>
      {adding ? <AccommodationModal eventId={eventId} data={d} onClose={() => setAdding(false)} /> : null}
      {from?.list === 'accommodation' ? (
        <AccommodationModal eventId={eventId} data={d} from={from} onClose={() => setFrom(null)} />
      ) : null}
      {from?.list === 'exception' ? (
        <ExceptionModal eventId={eventId} data={d} x={null} from={from} onClose={() => setFrom(null)} />
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------- travel exceptions

function ExceptionModal({
  eventId,
  data,
  x,
  from,
  onClose,
}: {
  eventId: string;
  data: DayOf;
  x: TravelException | null;
  from?: FormRequest | null;
  onClose: () => void;
}) {
  const [f, setF] = useState({
    inviteeId: x?.inviteeId ?? from?.inviteeId ?? '',
    kind: x?.kind ?? 'early_arrival_own_cost',
    request: x?.request ?? from?.text ?? '',
    decision: x?.decision ?? 'pending',
    decidedOn: x?.decidedOn ?? '',
    decidedBy: x?.decidedBy ?? '',
    conditions: x?.conditions ?? '',
    paymentEntryId: x?.paymentEntryId ?? '',
  });
  const set = (k: keyof typeof f, v: string) => setF((y) => ({ ...y, [k]: v }));
  const save = useDayOfWrite(eventId, async () => {
    if (!x) {
      const created = (
        await api.post(`/events/invitees/${f.inviteeId}/travel-exceptions`, { kind: f.kind, request: f.request })
      ).data.data as TravelException;
      if (f.decision !== 'pending' || f.decidedBy || f.decidedOn || f.conditions)
        await api.patch(`/events/invitees/${f.inviteeId}/travel-exceptions/${created.id}`, {
          decision: f.decision,
          decidedOn: f.decidedOn || null,
          decidedBy: f.decidedBy || null,
          conditions: f.conditions,
        });
      return created;
    }
    return api.patch(`/events/invitees/${x.inviteeId}/travel-exceptions/${x.id}`, {
      kind: f.kind,
      request: f.request,
      decision: f.decision,
      decidedOn: f.decidedOn || null,
      decidedBy: f.decidedBy || null,
      conditions: f.conditions,
      paymentEntryId: f.paymentEntryId || null,
    });
  });
  return (
    <Modal
      title={x ? `Travel exception: ${x.name}` : 'Log a travel exception'}
      why="Record what was asked and the NPI’s decision. Once approved, link the payment so the reimbursement matches."
      onClose={onClose}
      width={680}
      footer={
        <>
          {dialogFoot(
            <Button
              variant="primary"
              disabled={save.isLoading || !f.inviteeId}
              onClick={() => save.mutate(undefined, { onSuccess: onClose })}
            >
              Save
            </Button>,
            onClose,
          )}
          {!f.inviteeId ? (
            <p className="basis-full text-small text-ink-3">Save turns on once you choose a person.</p>
          ) : null}
        </>
      }
    >
      {save.isError ? <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice> : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!x ? (
          <Field title="Who">
            <select className={inputClass} value={f.inviteeId} onChange={(e) => set('inviteeId', e.target.value)}>
              <option value="">Choose a person</option>
              {(data.people ?? []).map((p) => (
                <option key={p.inviteeId} value={p.inviteeId}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field title="What" wide={Boolean(x)}>
          <select className={inputClass} value={f.kind} onChange={(e) => set('kind', e.target.value)}>
            {data.kinds.exceptions.map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
          </select>
        </Field>
        <Field title="What they asked for" wide>
          <textarea
            className={inputClass}
            rows={3}
            value={f.request}
            onChange={(e) => set('request', e.target.value)}
          />
        </Field>
        <Field title="The NPI’s decision">
          <select className={inputClass} value={f.decision} onChange={(e) => set('decision', e.target.value)}>
            {data.kinds.decisions.map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Decided on">
          <input
            type="date"
            className={inputClass}
            value={f.decidedOn}
            onChange={(e) => set('decidedOn', e.target.value)}
          />
        </Field>
        <Field title="Decided by" hint="Leave empty if it was the NPI.">
          <input className={inputClass} value={f.decidedBy} onChange={(e) => set('decidedBy', e.target.value)} />
        </Field>
        <Field title="Conditions" hint="For example, equal cost or own expense for the extra night.">
          <input className={inputClass} value={f.conditions} onChange={(e) => set('conditions', e.target.value)} />
        </Field>
        {x ? (
          <Field title="Their payment in the ledger" wide hint="Linked, the reimbursement matches the decision.">
            <select
              className={inputClass}
              value={f.paymentEntryId}
              onChange={(e) => set('paymentEntryId', e.target.value)}
            >
              <option value="">Not linked</option>
              {x.payments.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.kind} · {p.status}
                  {p.amountCents != null ? ` · ${money(p.amountCents)}` : ''}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}

/** Extended stays, extra airfare, early arrivals, children: what was asked, what the NPI decided, and the payment it changes. */
export function TravelExceptionsPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useDayOf(eventId);
  const leadShown = usePresent(`dayof-lead:${eventId}`);
  const [editing, setEditing] = useState<TravelException | 'new' | null>(null);
  if (!q.data) return <Loading q={q} title="Travel exceptions" />;
  const d = q.data;
  const label = (k: string) => d.kinds.exceptions.find((x) => x.key === k)?.label ?? k;
  const decision = (k: Decision) => d.kinds.decisions.find((x) => x.key === k)?.label ?? k;
  const list = d.exceptions ?? [];
  return (
    <Panel>
      <PanelHeader title="Travel exceptions" note={`${fmt(d.counts.exceptions.pending)} waiting for the NPI`}>
        {d.visible && canEdit ? <Button onClick={() => setEditing('new')}>Log one</Button> : null}
      </PanelHeader>
      <PanelBody>
        {!d.visible ? (
          leadShown ? null : (
            <InfoNotice>Requests and decisions are shown to admins.</InfoNotice>
          )
        ) : !list.length ? (
          <EmptyState title="No exceptions logged">
            Log an extended stay at equal cost, extra airfare, a hotel for someone usually flight-only, an early arrival
            at their own cost or a partner, child or companion travelling along, with the NPI’s decision.
          </EmptyState>
        ) : (
          <>
            <table className={stack.table}>
              <thead className={stack.thead}>
                <tr className={headRow}>
                  <th className={`${th} w-[240px]`}>Decision</th>
                  <th className={`${th} w-[24%]`}>Who</th>
                  <th className={th}>What they asked</th>
                  <th className={`${th} w-[80px]`}>
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody className={stack.tbody}>
                {list.map((x) => (
                  <tr key={x.id} className={stack.tr}>
                    <td className={`${td} ${stack.td}`}>
                      <Pill tone={DECISION_TONE[x.decision]}>{decision(x.decision)}</Pill>
                      {x.payment ? (
                        <span className={em}>
                          Payment: {x.payment.kind}, {x.payment.status}
                        </span>
                      ) : x.decision !== 'pending' && x.decision !== 'declined' ? (
                        <span className="block mt-1">
                          <Pill tone="problem" small>
                            Not linked to a payment
                          </Pill>
                        </span>
                      ) : null}
                    </td>
                    <td className={`${td} ${stack.td} font-semibold`}>
                      {x.name}
                      <span className={`${em} font-normal`}>{label(x.kind)}</span>
                    </td>
                    <td className={`${td} ${stack.td} ${stack.labelled} text-ink-2`} data-label="Asked">
                      {x.request ? quote(x.request) : <span className="text-ink-3">–</span>}
                      {x.conditions ? <span className={em}>Conditions: {x.conditions}</span> : null}
                      {x.decidedOn || x.decidedBy ? (
                        <span className={em}>
                          Decided by {x.decidedBy || 'the NPI'}
                          {x.decidedOn ? `, ${dayLabel(x.decidedOn)}` : ''}
                        </span>
                      ) : null}
                    </td>
                    <td className={`${td} ${stack.td} ${stack.acts}`}>
                      {canEdit ? (
                        <Button size="sm" onClick={() => setEditing(x)}>
                          Edit
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-small text-ink-3 mt-3 max-w-[72ch]">
              Once approved, link each exception to its payment so the reimbursement matches. An approved one with no
              payment is flagged.
            </p>
          </>
        )}
      </PanelBody>
      {editing ? (
        <ExceptionModal
          eventId={eventId}
          data={d}
          x={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Panel>
  );
}

// ---------------------------------------------------------------- the During view

interface Today {
  date: string;
  visible: boolean;
  parking?: {
    date: string;
    garages: { key: string; name: string; total: number; assigned: number; left: number }[];
    needed: number;
  };
  shifts?: {
    id: string;
    person: string;
    station: string;
    start: string | null;
    end: string | null;
    session: string | null;
    room: string | null;
  }[];
  arrivals?: BoardDay | null;
  departures?: BoardDay | null;
  accommodations?: Accommodation[];
}

/** On the day: who is on duty where, who arrives and leaves, what must be in place. Phone-friendly. */
export function DayOfToday({ eventId, day }: SimplePanelProps) {
  const date = day ?? '';
  const q = useQuery<Today>(
    ['event-dayof-today', eventId, date],
    async () => (await api.get(`/events/${eventId}/dayof/today/${date}`)).data.data,
    { enabled: Boolean(date), refetchInterval: 60000 },
  );
  if (!date) return null;
  if (q.isLoading) return <SkeletonRows rows={3} />;
  if (q.isError || !q.data) return <ErrorNotice>{errorMessage(q.error, 'Could not load the day.')}</ErrorNotice>;
  const t = q.data;
  const parkingLeft = t.parking?.garages.reduce((n, g) => n + g.left, 0) ?? 0;
  return (
    <Panel>
      <PanelHeader title={date === torontoToday() ? 'On duty and logistics today' : 'On duty and logistics'} />
      <PanelBody className="text-table flex flex-col gap-3">
        {!t.visible ? (
          <InfoNotice>The roster, arrivals and accommodations are shown to admins.</InfoNotice>
        ) : (
          <>
            <div>
              <div className="font-semibold">On duty</div>
              {t.shifts?.length ? (
                <ul>
                  {t.shifts.map((s) => (
                    <li key={s.id} className="border-b border-line py-[4px]">
                      <span className="tabular text-ink-2 mr-2">{time(s.start, s.end) || 'All day'}</span>
                      <span className="font-semibold">{s.person}</span> · {s.station}
                      {s.session ? ` · ${s.session}` : ''}
                      {s.room ? ` (${s.room})` : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-ink-2">Nobody on the roster for today.</p>
              )}
            </div>
            {t.accommodations?.length ? (
              <div>
                <div className="font-semibold">In place today</div>
                <ul>
                  {t.accommodations.map((a) => (
                    <li key={a.id} className="border-b border-line py-[3px]">
                      {STATUS_LABEL[a.status]} · {a.kind.replace(/_/g, ' ')} · {a.name ?? 'all attendees'}
                      {a.outcome ? ` · ${a.outcome}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {[
              ['Arriving', t.arrivals],
              ['Leaving', t.departures],
            ].map(([label, b]) =>
              (b as BoardDay | null) ? (
                <div key={label as string}>
                  <div className="font-semibold">
                    {label as string} ({fmt((b as BoardDay).count)})
                  </div>
                  <ul>
                    {(b as BoardDay).places.flatMap((pl) =>
                      pl.people.map((p) => (
                        <li key={`${pl.via}${p.key}`} className="border-b border-line py-[3px]">
                          <span className="tabular text-ink-2 mr-2">{p.time ?? '—'}</span>
                          {p.name} · {pl.via}
                          {p.ref ? ` · ${p.ref}` : ''}
                        </li>
                      )),
                    )}
                  </ul>
                </div>
              ) : null,
            )}
          </>
        )}
        {t.parking && t.parking.needed ? (
          <div className="text-ink-2">
            Parking: {fmt(t.parking.needed)} need a code today · {fmt(parkingLeft)} spare codes
          </div>
        ) : null}
      </PanelBody>
    </Panel>
  );
}
